import React, { useState, useEffect, useCallback } from 'react'
import { Routes, Route, useNavigate, useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { sendAdminAlert, sendClientConfirmation, sendStatusUpdate } from '../lib/email'
import toast from 'react-hot-toast'
import JobMessages from '../components/JobMessages'

// ── STATE ABBREVIATIONS ───────────────────────────────────────
const ST_ABBR = { 'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Plus Jakarta Sans':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY' }

// ── STATUS CONFIG ─────────────────────────────────────────────
const STATUS_CONFIG = {
  new:                  { label:'Order Received',        color:'#41427a', bg:'#EEF0F8', dot:'#4D4E8D'  },
  awaiting_assignment:  { label:'Order Received',        color:'#41427a', bg:'#EEF0F8', dot:'#4D4E8D'  },
  payment_received:     { label:'Payment Confirmed',     color:'#1A4A2E', bg:'#EAF2EE', dot:'#5E8A72'  },
  needs_assignment:     { label:'Finding Your Notary',   color:'#185FA5', bg:'#EBF4FF', dot:'#5BA5E5'  },
  assigned:             { label:'Notary Assigned',       color:'#1A4A6B', bg:'#E8F4FF', dot:'#2D7AB5'  },
  pending_acceptance:   { label:'Notary Notified',       color:'#1A4A6B', bg:'#E8F4FF', dot:'#2D7AB5'  },
  notary_accepted:      { label:'Notary Accepted',       color:'#4A1A6B', bg:'#F0E8FF', dot:'#8B5CF6'  },
  confirmed:            { label:'Appointment Confirmed', color:'#1A3A6B', bg:'#E8F0FF', dot:'#3B82F6'  },
  appointment_confirmed:{ label:'Appointment Confirmed', color:'#1A3A6B', bg:'#E8F0FF', dot:'#3B82F6'  },
  signing_completed:    { label:'Signing Completed',     color:'#1A4A2E', bg:'#EAF2EE', dot:'#5E8A72'  },
  completed:            { label:'Signing Completed',     color:'#1A4A2E', bg:'#EAF2EE', dot:'#5E8A72'  },
  closed:               { label:'Order Closed',          color:'#3A3D40', bg:'#F5F6F7', dot:'#A2A6AA'  },
  cancelled:            { label:'Cancelled',             color:'#8B1A1A', bg:'#FDF2F2', dot:'#E24B4A'  },
}

// ── STYLES ────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  .ind-wrap { min-height:100vh; background:linear-gradient(180deg,#F5F6F7 0%,#fff 100%); font-family:'Plus Jakarta Sans',sans-serif; }
  .ind-nav { background:#FFFFFF; border-bottom:1px solid #E2E4E7; padding:14px 24px; display:flex; align-items:center; justify-content:space-between; }
  .ind-nav-brand { font-family:'Plus Jakarta Sans',sans-serif; font-size:20px; color:#4D4E8D; text-decoration:none; }
  .ind-nav-sub { font-size:8px; color:#8A8F94; letter-spacing:0.12em; display:block; }
  .ind-nav-links { display:flex; gap:16px; align-items:center; }
  .ind-nav-link { color:#5C6065; font-size:12px; text-decoration:none; transition:color 0.2s; }
  .ind-nav-link:hover { color:#4D4E8D; }

  /* ── MOBILE NAV ── */
  @media(max-width:480px){
    .ind-nav { padding:12px 16px; }
    .ind-nav-brand { font-size:16px; }
    .ind-nav-sub { display:none; }
    .ind-nav-links { gap:10px; }
    .ind-nav-link { font-size:11px; }
  }

  .ind-container { max-width:720px; margin:0 auto; padding:32px 20px 64px; }
  .ind-card { background:#fff; border:1px solid #E2E4E7; border-radius:16px; overflow:hidden; margin-bottom:16px; }
  .ind-card-header { padding:16px 20px; background:#F5F6F7; border-bottom:1px solid #E2E4E7; }
  .ind-card-title { font-size:14px; font-weight:700; color:#151715; }
  .ind-card-body { padding:20px; }
  .ind-field { margin-bottom:14px; }
  .ind-field:last-child { margin-bottom:0; }
  .ind-label { font-size:10px; font-weight:700; color:#A2A6AA; letter-spacing:0.08em; display:block; margin-bottom:4px; }
  .ind-value { font-size:14px; color:#151715; }
  .ind-input { width:100%; padding:11px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:13px; font-family:inherit; transition:border-color 0.2s; background:#fff; }
  .ind-input:focus { outline:none; border-color:#4D4E8D; box-shadow:0 0 0 3px rgba(77,78,141,0.1); }
  .ind-select { width:100%; padding:11px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:13px; font-family:inherit; background:#fff; cursor:pointer; }
  .ind-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  @media(max-width:580px){ .ind-grid-2 { grid-template-columns:1fr; } }
  .ind-full { grid-column:1/-1; }

  /* BUTTONS */
  .ind-btn-primary { width:100%; padding:14px; border-radius:12px; border:none; background:linear-gradient(135deg,#4D4E8D,#41427a); color:#fff; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; transition:all 0.2s; letter-spacing:0.04em; }
  .ind-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(21,23,21,0.3); }
  .ind-btn-primary:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
  .ind-btn-outline { width:100%; padding:12px; border-radius:12px; border:1.5px solid #D7DADE; background:#fff; color:#151715; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:all 0.2s; }
  .ind-btn-outline:hover { border-color:#4D4E8D; }

  /* ✅ CTA PASS — gold gradient button, matches platform standard */
  .ind-btn-gold { background:linear-gradient(135deg,#41427a 0%,#4D4E8D 50%,#41427a 100%); color:#fff; border:none; padding:10px 20px; border-radius:10px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; letter-spacing:0.04em; box-shadow:0 4px 12px rgba(21,23,21,0.3); transition:transform 0.15s, box-shadow 0.2s; white-space:nowrap; }
  .ind-btn-gold:hover { transform:translateY(-2px); box-shadow:0 6px 18px rgba(21,23,21,0.4); }
  .ind-btn-gold:disabled { opacity:0.6; cursor:not-allowed; transform:none; }

  .ind-status-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:20px; font-size:12px; font-weight:700; }
  .ind-timeline { list-style:none; padding:0; margin:0; }
  .ind-timeline-item { display:flex; gap:12px; padding:10px 0; position:relative; }
  .ind-timeline-item::before { content:''; position:absolute; left:7px; top:28px; bottom:-10px; width:2px; background:#E2E4E7; }
  .ind-timeline-item:last-child::before { display:none; }
  .ind-timeline-dot { width:16px; height:16px; border-radius:50%; flex-shrink:0; margin-top:2px; }
  .ind-timeline-status { font-size:12px; font-weight:600; color:#151715; }
  .ind-timeline-meta { font-size:11px; color:#A2A6AA; margin-top:2px; }

  /* ORDER CARDS */
  .ind-order-card { border:1px solid #E2E4E7; border-radius:14px; padding:18px 20px; margin-bottom:12px; cursor:pointer; transition:all 0.2s; background:#fff; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
  .ind-order-card:hover { border-color:#4D4E8D; box-shadow:0 4px 16px rgba(77,78,141,0.1); }

  /* ── MOBILE ORDER CARD — price/date stack gracefully ── */
  @media(max-width:480px){
    .ind-order-card { padding:14px 16px; gap:10px; }
    .ind-order-card-right { text-align:left !important; margin-top:4px; }
  }

  .ind-progress-steps { display:flex; gap:0; margin-bottom:24px; overflow-x:auto; padding-bottom:4px; }
  .ind-progress-step { display:flex; flex-direction:column; align-items:center; flex:1; min-width:60px; position:relative; }
  .ind-progress-step::after { content:''; position:absolute; top:12px; left:50%; width:100%; height:2px; background:#E2E4E7; z-index:0; }
  .ind-progress-step:last-child::after { display:none; }
  .ind-progress-dot { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; position:relative; z-index:1; flex-shrink:0; }
  .ind-progress-label { font-size:9px; color:#A2A6AA; text-align:center; margin-top:6px; max-width:64px; line-height:1.3; }
  .ind-notary-card { background:linear-gradient(135deg,#4D4E8D,#41427a); border-radius:14px; padding:20px; color:#fff; }

  /* ── MOBILE NOTARY CARD BUTTONS ── */
  @media(max-width:480px){
    .ind-notary-actions { flex-direction:column !important; gap:8px !important; }
    .ind-notary-actions a { justify-content:center; }
  }

  .ind-footer { background:#4D4E8D; padding:24px; text-align:center; margin-top:auto; }
  .ind-footer-copy { font-size:11px; color:rgba(255,255,255,0.3); }
`

// ── ADDRESS AUTOCOMPLETE ──────────────────────────────────────
function AddressAutocomplete({ value, onChange, onSelect, placeholder, className }) {
  const [suggestions, setSuggestions] = React.useState([])
  const [open,        setOpen]        = React.useState(false)
  const [loading,     setLoading]     = React.useState(false)
  const timerRef = React.useRef(null)

  React.useEffect(() => {
    if (!value || value.length < 4) { setSuggestions([]); setOpen(false); return }
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res  = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=us&q=${encodeURIComponent(value)}`, { headers:{ 'Accept-Language':'en-US' } })
        const data = await res.json()
        setSuggestions(data)
        setOpen(data.length > 0)
      } catch { setSuggestions([]) }
      finally { setLoading(false) }
    }, 400)
    return () => clearTimeout(timerRef.current)
  }, [value])

  function pick(item) {
    const a    = item.address || {}
    const road = a.road || a.pedestrian || a.footway || ''
    const num  = a.house_number || ''
    const addr = num ? `${num} ${road}` : road
    const city = a.city || a.town || a.village || a.hamlet || a.county || ''
    const st   = a.state || ''
    const zip  = a.postcode || ''
    const stAbbr = st.length === 2 ? st : (ST_ABBR[st] || st)
    onChange(addr)
    onSelect({ address:addr, city, state:stAbbr, zip })
    setSuggestions([])
    setOpen(false)
  }

  return (
    <div style={{ position:'relative' }}>
      <input className={className} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Start typing address...'} autoComplete="off"
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)} />
      {loading && <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#4D4E8D' }}>🔍</span>}
      {open && suggestions.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #4D4E8D', borderRadius:10, zIndex:999, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', marginTop:2, maxHeight:220, overflowY:'auto' }}>
          {suggestions.map((s, i) => (
            <button key={i} type="button" onMouseDown={() => pick(s)}
              style={{ width:'100%', padding:'10px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left', fontSize:12, color:'#151715', borderBottom:'1px solid #EEEFF1', fontFamily:'inherit' }}
              onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              📍 {s.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── PROGRESS TRACKER ──────────────────────────────────────────
function ProgressTracker({ status }) {
  const steps = [
    { key:'payment_received', label:'Payment Confirmed' },
    { key:'assigned',         label:'Notary Assigned'   },
    { key:'confirmed',        label:'Appt Confirmed'    },
    { key:'completed',        label:'Signing Done'      },
    { key:'closed',           label:'Order Closed'      },
  ]
  const statusMap = {
    new:'payment_received', awaiting_assignment:'payment_received',
    payment_received:'payment_received', needs_assignment:'payment_received',
    assigned:'assigned', pending_acceptance:'assigned', notary_accepted:'assigned',
    confirmed:'confirmed', appointment_confirmed:'confirmed',
    signing_completed:'completed', completed:'completed',
    closed:'closed',
  }
  const current = statusMap[status] || 'payment_received'
  const idx     = steps.findIndex(s => s.key === current)

  return (
    <div className="ind-progress-steps">
      {steps.map((s, i) => {
        const done   = i < idx
        const active = i === idx
        return (
          <div key={s.key} className="ind-progress-step">
            <div className="ind-progress-dot" style={{
              background: done ? '#5E8A72' : active ? '#4D4E8D' : '#E2E4E7',
              color:      done || active ? '#fff' : '#A2A6AA',
            }}>
              {done ? '✓' : i+1}
            </div>
            <div className="ind-progress-label" style={{ color: active ? '#151715' : done ? '#5E8A72' : '#A2A6AA', fontWeight: active ? 700 : 400 }}>
              {s.label}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── REQUEST FORM ──────────────────────────────────────────────
function RequestForm() {
  const { user, profile } = useAuth()
  const navigate          = useNavigate()
  const [services,    setServices]    = useState([])
  const [submitting,  setSubmitting]  = useState(false)
  const [submitted,   setSubmitted]   = useState(null)
  const [form, setForm] = useState({
    full_name:        profile?.full_name || '',
    email:            user?.email || '',
    phone:            profile?.phone || '',
    signing_address:  '',
    signing_city:     '',
    signing_state:    'FL',
    signing_zip:      '',
    signing_type:     '',
    scheduled_at:     '',
    notes:            '',
  })

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  useEffect(() => {
    supabase.from('services').select('*').order('name').then(({ data }) => {
      setServices(data || [])
      if (data?.length > 0) set('signing_type', data[0].name)
    })
  }, [])

  useEffect(() => {
    if (profile) {
      set('full_name', profile.full_name || '')
      set('phone', profile.phone || '')
      if (user?.email) set('email', user.email)
    }
  }, [profile, user])

  const selectedService = services.find(s => s.name === form.signing_type)
  const price           = selectedService?.client_price || 0

  async function submit(e) {
    e.preventDefault()
    if (!form.full_name || !form.signing_address || !form.signing_type) {
      toast.error('Please fill in all required fields!!')
      return
    }
    if (!user) { toast.error('Please sign in to submit a request!!'); return }
    setSubmitting(true)
    try {
      const fullAddress = `${form.signing_address}, ${form.signing_city}, ${form.signing_state} ${form.signing_zip}`

      const { data, error } = await supabase.from('jobs').insert({
        job_type:             'individual',
        individual_user_id:   user.id,
        client_id:            null,
        borrower_name:        form.full_name,
        borrower_phone:       form.phone,
        borrower_email:       form.email,
        signing_type:         form.signing_type,
        signing_address:      form.signing_address,
        signing_city:         form.signing_city,
        signing_state:        form.signing_state,
        signing_zip:          form.signing_zip,
        scheduled_at:         form.scheduled_at || null,
        special_instructions: form.notes || null,
        total_fee:            price,
        notary_pay:           price > 0 ? Math.round(price * 0.75) : 0,
        lca_cut:              price > 0 ? Math.round(price * 0.25) : 0,
        status:               'new',
      }).select().single()

      if (error) throw error

      await supabase.from('job_payments').insert({
        job_id:                data.id,
        client_invoice_amount: price,
        notary_pay_amount:     price > 0 ? Math.round(price * 0.75) : 0,
        client_payment_status: 'pending',
      })

      await supabase.from('job_status_events').insert({
        job_id:          data.id,
        status:          'new',
        from_status:     null,
        created_by_role: 'individual',
        created_by:      user.id,
        note:            'Order submitted by individual',
      })

      await sendAdminAlert({
        client_name:   form.full_name,
        client_phone:  form.phone || '—',
        client_email:  form.email || '—',
        service_name:  form.signing_type,
        amount:        price,
        address:       fullAddress,
        appointment:   form.scheduled_at ? new Date(form.scheduled_at).toLocaleString() : 'Flexible',
        is_veteran:    false,
        special_notes: `INDIVIDUAL ORDER · ${form.notes || 'No notes'}`,
      })

      if (form.email) {
        await sendClientConfirmation({
          client_name:  form.full_name,
          client_email: form.email,
          service_name: form.signing_type,
          amount:       price,
          job_number:   data.job_number,
          address:      fullAddress,
          appointment:  form.scheduled_at ? new Date(form.scheduled_at).toLocaleString() : null,
        })
      }

      setSubmitted(data)
    } catch(err) { toast.error(err.message) }
    finally { setSubmitting(false) }
  }

  if (submitted) return (
    <>
      <style>{STYLES}</style>
      <div className="ind-wrap">
        <nav className="ind-nav">
          <Link to="/order" className="ind-nav-brand">
            Notary Signing Vault<span className="ind-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </Link>
        </nav>
        <div className="ind-container" style={{ textAlign:'center', paddingTop:64 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
          <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:32, color:'#151715', fontWeight:600, marginBottom:8 }}>
            Request Submitted!!
          </div>
          <div style={{ fontSize:14, color:'#5C6065', marginBottom:8, lineHeight:1.7 }}>
            Order <strong style={{ color:'#151715' }}>#{submitted.job_number}</strong> received!!<br />
            Our team is matching you with a certified NSV notary right now!!
          </div>
          {form.email && (
            <div style={{ fontSize:12, color:'#A2A6AA', marginBottom:32 }}>
              A confirmation has been sent to <strong>{form.email}</strong>
            </div>
          )}
          <button className="ind-btn-primary" onClick={() => navigate('/order/track/' + submitted.id)} style={{ maxWidth:320, margin:'0 auto' }}>
            Track My Order →
          </button>
          <button className="ind-btn-outline" onClick={() => navigate('/order')} style={{ maxWidth:320, margin:'12px auto 0' }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    </>
  )

  return (
    <>
      <style>{STYLES}</style>
      <div className="ind-wrap">
        <nav className="ind-nav">
          <Link to="/order" className="ind-nav-brand">
            Notary Signing Vault<span className="ind-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </Link>
          <div className="ind-nav-links">
            <Link to="/order" className="ind-nav-link">My Orders</Link>
            {!user && <Link to="/login" className="ind-nav-link" style={{ color:'#4D4E8D', fontWeight:600 }}>Sign In</Link>}
          </div>
        </nav>

        <div className="ind-container">
          <div style={{ marginBottom:28 }}>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#151715', fontWeight:600, marginBottom:6 }}>
              Request a Notary
            </div>
            <div style={{ fontSize:13, color:'#5C6065' }}>
              All-inclusive pricing · No hidden fees · Certified mobile notaries nationwide
            </div>
          </div>

          <form onSubmit={submit} noValidate>

            {/* YOUR INFO */}
            <div className="ind-card">
              <div className="ind-card-header">
                <div className="ind-card-title">👤 Your Information</div>
              </div>
              <div className="ind-card-body">
                <div className="ind-grid-2">
                  <div className="ind-field">
                    <label className="ind-label">FULL NAME <span style={{ color:'#E24B4A' }}>*</span></label>
                    <input className="ind-input" value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Jane Smith" required />
                  </div>
                  <div className="ind-field">
                    <label className="ind-label">PHONE NUMBER</label>
                    <input className="ind-input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(904) 555-0100" />
                  </div>
                  <div className="ind-field ind-full">
                    <label className="ind-label">EMAIL ADDRESS</label>
                    <input className="ind-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@email.com" />
                  </div>
                </div>
              </div>
            </div>

            {/* SERVICE */}
            <div className="ind-card">
              <div className="ind-card-header">
                <div className="ind-card-title">📋 Service Needed</div>
              </div>
              <div className="ind-card-body">
                <div className="ind-field">
                  <label className="ind-label">TYPE OF NOTARIZATION <span style={{ color:'#E24B4A' }}>*</span></label>
                  <select className="ind-select" value={form.signing_type} onChange={e => set('signing_type', e.target.value)} required>
                    {services.length > 0
                      ? services.map(s => <option key={s.id} value={s.name}>{s.name}</option>)
                      : <option>Loading services...</option>
                    }
                  </select>
                </div>
                {selectedService && price > 0 && (
                  <div style={{ background:'#EAF2EE', border:'1px solid rgba(45,122,79,0.2)', borderRadius:10, padding:'12px 16px', marginTop:8 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#5E8A72', letterSpacing:'0.06em', marginBottom:4 }}>ALL-INCLUSIVE PRICE</div>
                    <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#151715', fontWeight:600 }}>${price}</div>
                    <div style={{ fontSize:11, color:'#4A7A5C' }}>✅ Travel, platform & convenience fees included!! No surprises!!</div>
                  </div>
                )}
              </div>
            </div>

            {/* APPOINTMENT */}
            <div className="ind-card">
              <div className="ind-card-header">
                <div className="ind-card-title">📅 Appointment Details</div>
              </div>
              <div className="ind-card-body">
                <div className="ind-field ind-full" style={{ marginBottom:14 }}>
                  <label className="ind-label">SIGNING ADDRESS <span style={{ color:'#E24B4A' }}>*</span></label>
                  <AddressAutocomplete
                    className="ind-input"
                    value={form.signing_address}
                    onChange={v => set('signing_address', v)}
                    onSelect={({ address, city, state, zip }) => {
                      set('signing_address', address)
                      if (city)  set('signing_city', city)
                      if (state) set('signing_state', state)
                      if (zip)   set('signing_zip', zip)
                    }}
                    placeholder="Start typing your address..."
                  />
                </div>
                <div className="ind-grid-2">
                  <div className="ind-field">
                    <label className="ind-label">CITY</label>
                    <input className="ind-input" value={form.signing_city} onChange={e => set('signing_city', e.target.value)} placeholder="Jacksonville" />
                  </div>
                  <div className="ind-field">
                    <label className="ind-label">STATE</label>
                    <input className="ind-input" value={form.signing_state} onChange={e => set('signing_state', e.target.value)} placeholder="FL" maxLength={2} />
                  </div>
                  <div className="ind-field">
                    <label className="ind-label">ZIP CODE</label>
                    <input className="ind-input" value={form.signing_zip} onChange={e => set('signing_zip', e.target.value)} placeholder="32202" maxLength={5} />
                  </div>
                  <div className="ind-field">
                    <label className="ind-label">PREFERRED DATE & TIME</label>
                    <input className="ind-input" type="datetime-local" value={form.scheduled_at} onChange={e => set('scheduled_at', e.target.value)} min={new Date().toISOString().slice(0,16)} style={{ width:'100%', fontSize:'16px', maxWidth:'100%' }} />
                  </div>
                  <div className="ind-field ind-full">
                    <label className="ind-label">NOTES (optional)</label>
                    <textarea className="ind-input" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any special instructions or questions..." rows={3} style={{ resize:'none' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* SUMMARY + SUBMIT */}
            {price > 0 && (
              <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, color:'#fff' }}>Order Summary</div>
                  <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#fff', fontWeight:600 }}>${price}</div>
                </div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginBottom:14 }}>
                  {form.signing_type} · {form.signing_city || 'Your location'}, {form.signing_state}
                  {form.scheduled_at && <> · {new Date(form.scheduled_at).toLocaleDateString()}</>}
                </div>
                <div style={{ fontSize:11, color:'rgba(77,78,141,0.7)', marginBottom:16, lineHeight:1.6 }}>
                  ✅ All-inclusive · Travel, platform & convenience fees included!!<br />
                  ✅ NSV will contact you to confirm appointment details!!
                </div>
                {/* ✅ CTA PASS — gold gradient, white text, standard platform style */}
                <button type="submit" className="ind-btn-gold" disabled={submitting} style={{ width:'100%', padding:14, fontSize:15 }}>
                  {submitting ? '⏳ Submitting...' : `Submit Request — $${price} →`}
                </button>
              </div>
            )}
            {price === 0 && (
              <button type="submit" className="ind-btn-primary" disabled={submitting}>
                {submitting ? '⏳ Submitting...' : 'Submit Request →'}
              </button>
            )}
          </form>
        </div>

        <footer className="ind-footer">
          <div className="ind-footer-copy">© 2026 Notary Signing Vault, LLC · DBA Nationwide Notary Professionals Network</div>
        </footer>
      </div>
    </>
  )
}

// ── ORDER DETAIL ──────────────────────────────────────────────
function OrderDetail() {
  const { jobId }      = useParams()
  const { user }       = useAuth()
  const navigate       = useNavigate()
  const [job,       setJob]       = useState(null)
  const [notary,    setNotary]    = useState(null)
  const [timeline,  setTimeline]  = useState([])
  const [payments,  setPayments]  = useState(null)
  const [loading,   setLoading]   = useState(true)

  const loadJob = useCallback(async () => {
    if (!jobId || !user) return
    const { data } = await supabase.from('jobs').select('*').eq('id', jobId).single()
    if (!data) { toast.error('Order not found!!'); navigate('/order'); return }
    if (data.individual_user_id !== user.id) { toast.error('Access denied!!'); navigate('/order'); return }
    setJob(data)

    if (data.notary_id) {
      const { data: p } = await supabase.from('profiles').select('*, notaries(*)').eq('id', data.notary_id).single()
      setNotary(p)
    }

    const { data: tl } = await supabase.from('job_status_events').select('*').eq('job_id', jobId).order('created_at', { ascending:true })
    setTimeline(tl || [])

    const { data: pay } = await supabase.from('job_payments').select('*').eq('job_id', jobId).maybeSingle()
    setPayments(pay)
    setLoading(false)
  }, [jobId, user, navigate])

  useEffect(() => { loadJob() }, [loadJob])

  useEffect(() => {
    if (!jobId) return
    const ch = supabase.channel(`individual-order-${jobId}`)
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'jobs', filter:`id=eq.${jobId}` },
        payload => {
          setJob(prev => ({ ...prev, ...payload.new }))
          const cfg = STATUS_CONFIG[payload.new.status]
          if (cfg) toast.success(`📬 Update: ${cfg.label}!!`, { duration:5000 })
          loadJob()
        })
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [jobId, loadJob])

  if (loading) return (
    <>
      <style>{STYLES}</style>
      <div className="ind-wrap" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
        <div style={{ textAlign:'center', color:'#A2A6AA' }}>Loading your order...</div>
      </div>
    </>
  )

  if (!job) return null

  const cfg = STATUS_CONFIG[job.status] || { label:job.status, color:'#5C6065', bg:'#F5F6F7', dot:'#A2A6AA' }

  return (
    <>
      <style>{STYLES}</style>
      <div className="ind-wrap">
        <nav className="ind-nav">
          <Link to="/order" className="ind-nav-brand">
            Notary Signing Vault<span className="ind-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </Link>
          <div className="ind-nav-links">
            <Link to="/order" className="ind-nav-link">← My Orders</Link>
          </div>
        </nav>

        <div className="ind-container">

          {/* ── ORDER HEADER — flex-wrap on mobile ── */}
          <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:16, padding:'22px 28px', marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
              <div>
                <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#fff', fontWeight:500, marginBottom:4 }}>
                  Order #{job.job_number}
                </div>
                <span className="ind-status-badge" style={{ background:cfg.bg, color:cfg.color }}>
                  {cfg.label}
                </span>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#fff', fontWeight:600 }}>${job.total_fee}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{job.signing_type}</div>
              </div>
            </div>
          </div>

          {/* PROGRESS */}
          <div className="ind-card">
            <div className="ind-card-header"><div className="ind-card-title">📊 Order Progress</div></div>
            <div className="ind-card-body">
              <ProgressTracker status={job.status} />
            </div>
          </div>

          {/* APPOINTMENT */}
          <div className="ind-card">
            <div className="ind-card-header"><div className="ind-card-title">📅 Appointment Details</div></div>
            <div className="ind-card-body">
              <div className="ind-grid-2">
                <div className="ind-field">
                  <span className="ind-label">DATE & TIME</span>
                  <div className="ind-value">
                    {job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : <span style={{ color:'#A2A6AA' }}>To be confirmed</span>}
                  </div>
                </div>
                <div className="ind-field">
                  <span className="ind-label">SERVICE</span>
                  <div className="ind-value">{job.signing_type}</div>
                </div>
                <div className="ind-field ind-full">
                  <span className="ind-label">SIGNING ADDRESS</span>
                  <div className="ind-value">
                    {job.signing_address}<br />{job.signing_city}, {job.signing_state} {job.signing_zip}
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${job.signing_address}, ${job.signing_city}, ${job.signing_state}`)}`}
                      target="_blank" rel="noreferrer"
                      style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, color:'#5E8A72', textDecoration:'none', marginLeft:10 }}>
                      🗺️ Open Maps
                    </a>
                  </div>
                </div>
              </div>
              {job.special_instructions && (
                <div style={{ background:'#EBF4FF', borderRadius:8, padding:'10px 14px', marginTop:10, fontSize:12, color:'#3A3D40' }}>
                  <strong>Notes:</strong> {job.special_instructions}
                </div>
              )}
            </div>
          </div>

          {/* NOTARY INFO */}
          <div className="ind-card">
            <div className="ind-card-header"><div className="ind-card-title">👤 Your Notary</div></div>
            <div className="ind-card-body">
              {notary ? (
                <div className="ind-notary-card">
                  <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14 }}>
                    <div style={{ width:48, height:48, borderRadius:'50%', background:'rgba(77,78,141,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:600, color:'#4D4E8D', flexShrink:0 }}>
                      {notary.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div style={{ fontSize:16, fontWeight:600, color:'#fff', marginBottom:2 }}>{notary.full_name}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)' }}>
                        {notary.notaries?.commission_state || ''} · {notary.notaries?.total_signings || 0} signings · ⭐ {notary.notaries?.avg_rating || '—'}
                      </div>
                    </div>
                  </div>
                  {/* ✅ MOBILE — notary action buttons stack vertically on small screens */}
                  <div className="ind-notary-actions" style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    {notary.phone && (
                      <a href={`tel:${notary.phone}`} style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(77,78,141,0.15)', color:'#4D4E8D', padding:'8px 14px', borderRadius:20, fontSize:12, fontWeight:500, textDecoration:'none' }}>
                        📞 {notary.phone}
                      </a>
                    )}
                    {notary.email && (
                      <a href={`mailto:${notary.email}`} style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.08)', color:'rgba(255,255,255,0.7)', padding:'8px 14px', borderRadius:20, fontSize:12, fontWeight:500, textDecoration:'none' }}>
                        ✉️ Email Notary
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign:'center', padding:'20px 0' }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>🔍</div>
                  <div style={{ fontSize:14, fontWeight:600, color:'#151715', marginBottom:4 }}>Finding your notary now!!</div>
                  <div style={{ fontSize:12, color:'#5C6065' }}>Our team is matching you with the best available certified notary in your area!!</div>
                </div>
              )}
            </div>
          </div>

          {/* PAYMENT */}
          <div className="ind-card">
            <div className="ind-card-header"><div className="ind-card-title">💳 Payment</div></div>
            <div className="ind-card-body">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #EEEFF1' }}>
                <span style={{ fontSize:13, color:'#5C6065' }}>Service</span>
                <span style={{ fontSize:14, fontWeight:600, color:'#151715' }}>{job.signing_type}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #EEEFF1' }}>
                <span style={{ fontSize:13, color:'#5C6065' }}>Total</span>
                <span style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:22, fontWeight:600, color:'#151715' }}>${job.total_fee}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0' }}>
                <span style={{ fontSize:13, color:'#5C6065' }}>Payment Status</span>
                <span style={{ fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20,
                  background: payments?.client_payment_status === 'paid' ? '#EAF2EE' : '#EEF0F8',
                  color:      payments?.client_payment_status === 'paid' ? '#5E8A72' : '#41427a' }}>
                  {payments?.client_payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* TIMELINE */}
          <div className="ind-card">
            <div className="ind-card-header"><div className="ind-card-title">🕐 Status History</div></div>
            <div className="ind-card-body">
              {timeline.length === 0 ? (
                <div style={{ textAlign:'center', padding:'16px 0', color:'#A2A6AA', fontSize:13 }}>No events yet!!</div>
              ) : (
                <ul className="ind-timeline">
                  {timeline.map((event, i) => {
                    const ecfg = STATUS_CONFIG[event.status] || { dot:'#A2A6AA', label:event.status }
                    return (
                      <li key={event.id || i} className="ind-timeline-item">
                        <div className="ind-timeline-dot" style={{ background:ecfg.dot }} />
                        <div>
                          <div className="ind-timeline-status">{ecfg.label || event.status}</div>
                          <div className="ind-timeline-meta">{new Date(event.created_at).toLocaleString()}</div>
                          {event.note && <div style={{ fontSize:11, color:'#5C6065', marginTop:2, fontStyle:'italic' }}>{event.note}</div>}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* MESSAGES */}
          <div className="ind-card">
            <div className="ind-card-header"><div className="ind-card-title">💬 Messages</div></div>
            <div style={{ padding:'0 20px 20px' }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#5C6065', margin:'16px 0 8px' }}>WITH NSV TEAM</div>
              <JobMessages jobId={jobId} jobNumber={job.job_number} role="individual" recipientRole="admin" />
              {notary && (
                <>
                  <div style={{ fontSize:12, fontWeight:600, color:'#5C6065', margin:'16px 0 8px' }}>WITH YOUR NOTARY</div>
                  <JobMessages jobId={jobId} jobNumber={job.job_number} role="individual" recipientRole="notary" />
                </>
              )}
            </div>
          </div>

        </div>

        <footer className="ind-footer">
          <div className="ind-footer-copy">© 2026 Notary Signing Vault, LLC · DBA Nationwide Notary Professionals Network</div>
        </footer>
      </div>
    </>
  )
}

// ── DASHBOARD ─────────────────────────────────────────────────
function IndividualDashboard() {
  const { user, profile } = useAuth()
  const navigate          = useNavigate()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    supabase.from('jobs').select('*')
      .eq('individual_user_id', user.id)
      .order('created_at', { ascending:false })
      .then(({ data }) => { setJobs(data || []); setLoading(false) })

    const ch = supabase.channel('individual-dashboard-realtime')
      .on('postgres_changes', { event:'*', schema:'public', table:'jobs', filter:`individual_user_id=eq.${user.id}` },
        payload => {
          if (payload.eventType === 'INSERT') setJobs(prev => [payload.new, ...prev])
          else if (payload.eventType === 'UPDATE') setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j))
        })
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [user])

  const active    = jobs.filter(j => !['closed','cancelled'].includes(j.status))
  const completed = jobs.filter(j => ['closed','cancelled'].includes(j.status))

  return (
    <>
      <style>{STYLES}</style>
      <div className="ind-wrap">
        <nav className="ind-nav">
          <Link to="/order" className="ind-nav-brand">
            Notary Signing Vault<span className="ind-nav-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </Link>
          <div className="ind-nav-links">
            <Link to="/order/request" className="ind-nav-link" style={{ color:'#4D4E8D', fontWeight:600 }}>+ New Request</Link>
          </div>
        </nav>

        <div className="ind-container">

          {/* WELCOME */}
          <div style={{ marginBottom:28 }}>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:26, color:'#151715', fontWeight:500, marginBottom:4 }}>
              Welcome back{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}!!
            </div>
            <div style={{ fontSize:13, color:'#5C6065' }}>Your notary orders in one place!!</div>
          </div>

          {/* ✅ CTA PASS — "Request Now →" gold gradient, white text, standard platform style */}
          <div onClick={() => navigate('/order/request')}
            style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:14, padding:'20px 24px', marginBottom:24, cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, color:'#fff', marginBottom:4 }}>Need a Notary?</div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)' }}>All-inclusive pricing · Certified notaries nationwide</div>
            </div>
            <button
              className="ind-btn-gold"
              onClick={e => { e.stopPropagation(); navigate('/order/request') }}
              aria-label="Request a notary now"
            >
              Request Now →
            </button>
          </div>

          {/* ACTIVE ORDERS */}
          {loading && <div style={{ textAlign:'center', padding:40, color:'#A2A6AA' }}>Loading your orders...</div>}

          {!loading && active.length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:'#A2A6AA', letterSpacing:'0.08em', marginBottom:12 }}>ACTIVE ORDERS</div>
              {active.map(j => {
                const jcfg = STATUS_CONFIG[j.status] || { label:j.status, color:'#5C6065', bg:'#F5F6F7' }
                return (
                  <div key={j.id} className="ind-order-card" onClick={() => navigate('/order/track/' + j.id)}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:600, color:'#151715', marginBottom:4 }}>#{j.job_number}</div>
                      <div style={{ fontSize:12, color:'#5C6065', marginBottom:6 }}>{j.signing_type} · {j.signing_city}, {j.signing_state}</div>
                      <span className="ind-status-badge" style={{ background:jcfg.bg, color:jcfg.color, fontSize:10 }}>{jcfg.label}</span>
                    </div>
                    {/* ✅ MOBILE — price/date right-align, stacks gracefully below 480px */}
                    <div className="ind-order-card-right" style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:20, color:'#151715', fontWeight:500 }}>${j.total_fee}</div>
                      <div style={{ fontSize:11, color:'#A2A6AA', marginTop:2 }}>{new Date(j.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                )
              })}
            </>
          )}

          {!loading && completed.length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:'#A2A6AA', letterSpacing:'0.08em', marginBottom:12, marginTop:24 }}>COMPLETED ORDERS</div>
              {completed.map(j => (
                <div key={j.id} className="ind-order-card" onClick={() => navigate('/order/track/' + j.id)} style={{ opacity:0.7 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, color:'#151715', marginBottom:2 }}>#{j.job_number}</div>
                    <div style={{ fontSize:12, color:'#5C6065' }}>{j.signing_type} · {j.signing_city}, {j.signing_state}</div>
                  </div>
                  <div style={{ fontSize:11, color:'#A2A6AA', flexShrink:0 }}>
                    {new Date(j.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </>
          )}

          {!loading && jobs.length === 0 && (
            <div style={{ textAlign:'center', padding:'48px 0' }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
              <div style={{ fontSize:15, fontWeight:600, color:'#151715', marginBottom:6 }}>No orders yet!!</div>
              <div style={{ fontSize:13, color:'#5C6065', marginBottom:20 }}>Request your first notary today!!</div>
              <button className="ind-btn-primary" onClick={() => navigate('/order/request')} style={{ maxWidth:240, margin:'0 auto' }}>
                Request a Notary →
              </button>
            </div>
          )}
        </div>

        <footer className="ind-footer">
          <div className="ind-footer-copy">© 2026 Notary Signing Vault, LLC · DBA Nationwide Notary Professionals Network</div>
        </footer>
      </div>
    </>
  )
}

// ── ROUTER ────────────────────────────────────────────────────
export default function IndividualPortal() {
  return (
    <Routes>
      <Route index             element={<IndividualDashboard />} />
      <Route path="request"    element={<RequestForm />} />
      <Route path="track/:jobId" element={<OrderDetail />} />
      <Route path="*"          element={<IndividualDashboard />} />
    </Routes>
  )
}
